/**
 * 🟥 SaveToGitHubModal — modal puro (sin botón).
 *
 * Recibe `open / onClose` y maneja todo el flujo OAuth + commit.
 * Usado por:
 *   - GitHubHeaderButton (header, siempre visible para premium/owner)
 *   - SaveToGitHubButton (Workspace, opcional)
 *
 * 🟥 FIX 2026-06-01: rutas RELATIVAS al backend (same-origin). NO usar
 * `REACT_APP_BACKEND_URL` porque en producción puede apuntar a otro host
 * y la cookie de sesión httpOnly se pierde → falsos `Not Authenticated`.
 *
 * NUNCA pide PAT manual — solo OAuth.
 */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Github, Loader2, ExternalLink, X, LogOut, UserPlus } from 'lucide-react';

const LAST_USERNAME_LS_KEY = 'damyxia_last_github_username';

// Sanitiza un nombre a formato válido de repo GitHub: minúsculas, guiones,
// sin caracteres especiales. Máximo 100 chars.
const sanitizeRepoName = (name) => {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 100);
};

const fetchJSON = async (path, opts = {}) => {
  // Same-origin: la cookie httpOnly se envía siempre.
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!res.ok) {
    const err = new Error(data?.detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

const SaveToGitHubModal = ({
  open,
  onClose,
  files = [],
  connected = false,
  scopeOk = false,
  onRefreshStatus,
  language = 'es',
  defaultRepoName = 'damyxia-project',
}) => {
  const es = language === 'es';
  const [ghLogin, setGhLogin] = useState(null);
  const [repos, setRepos] = useState([]);
  const [mode, setMode] = useState('new');
  const [newRepoName, setNewRepoName] = useState(defaultRepoName);
  const [privateRepo, setPrivateRepo] = useState(true);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [commitMessage, setCommitMessage] = useState(
    es ? 'Commit inicial desde DamyxIA' : 'Initial commit from DamyxIA'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // 🟥 P19.1 · Feature "auto-conectar": recordar la última cuenta GitHub
  // conectada. Al abrir el modal (aún sin conectar) mostramos un banner
  // "última cuenta usada: @X" para que el usuario reconecte con 1 click.
  const [lastKnownUsername, setLastKnownUsername] = useState(() => {
    try {
      return window.localStorage?.getItem(LAST_USERNAME_LS_KEY) || null;
    } catch { return null; }
  });

  const loadRepos = useCallback(async () => {
    setError(null);
    if (!connected || !scopeOk) return;
    try {
      const status = await fetchJSON('/api/github/status');
      setGhLogin(status.username || null);
      // 🟥 P19.1: persistir el username para próxima apertura del modal.
      if (status.username) {
        try {
          window.localStorage?.setItem(LAST_USERNAME_LS_KEY, status.username);
          setLastKnownUsername(status.username);
        } catch { /* localStorage bloqueado — ignorar */ }
      }
      const repoList = await fetchJSON('/api/github/repos');
      const arr = Array.isArray(repoList) ? repoList : [];
      setRepos(arr);
      if (arr.length > 0 && !selectedRepo) setSelectedRepo(arr[0].full_name);
    } catch (e) {
      setError(es ? 'Error al cargar repositorios' : 'Failed to load repositories');
    }
  }, [connected, scopeOk, es, selectedRepo]);

  useEffect(() => {
    if (open) loadRepos();
  }, [open, loadRepos]);

  const hasActiveProject = Array.isArray(files) && files.length > 0;

  const handleConnect = async () => {
    setError(null);
    try {
      const data = await fetchJSON('/api/github/oauth/start');
      if (!data?.authorize_url) {
        throw new Error(es ? 'Respuesta inválida del servidor' : 'Invalid server response');
      }
      window.location.href = data.authorize_url;
    } catch (e) {
      const detail = e?.data?.detail || e?.message || '';
      let msg;
      if (detail.includes('github_oauth_not_configured')) {
        msg = es
          ? 'OAuth de GitHub no está configurado en el servidor. Revisa las variables GITHUB_CLIENT_ID y GITHUB_CLIENT_SECRET.'
          : 'GitHub OAuth not configured on the server. Check GITHUB_CLIENT_ID and GITHUB_CLIENT_SECRET.';
      } else if (detail.includes('github_premium_required')) {
        msg = es ? 'Esta función es solo para Premium' : 'This feature is Premium-only';
      } else {
        msg = (es ? 'Error al iniciar OAuth: ' : 'OAuth start failed: ') + detail;
      }
      setError(msg);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    try {
      await fetchJSON('/api/github/disconnect', { method: 'DELETE' });
      setRepos([]);
      setGhLogin(null);
      onRefreshStatus?.();
    } catch (e) {
      setError(es ? 'Error al desconectar' : 'Failed to disconnect');
    }
  };

  // 🟥 P19.2 · Botón "Cambiar de cuenta": desconecta + limpia el username
  // recordado en localStorage + inicia OAuth de nuevo, permitiendo elegir
  // otra cuenta de GitHub. Ideal cuando el usuario tiene varias cuentas
  // (personal / trabajo) y quiere alternar.
  const handleSwitchAccount = async () => {
    setError(null);
    setLoading(true);
    try {
      // 1. Desconectar la cuenta actual del backend
      await fetchJSON('/api/github/disconnect', { method: 'DELETE' });
      // 2. Limpiar el username recordado (para que no sugiera reconnect
      //    con la misma cuenta que se acaba de desconectar)
      try {
        window.localStorage?.removeItem(LAST_USERNAME_LS_KEY);
        setLastKnownUsername(null);
      } catch (_) { /* localStorage bloqueado */ }
      // 3. Reset del estado local
      setRepos([]);
      setGhLogin(null);
      onRefreshStatus?.();
      // 4. Iniciar OAuth de nuevo (permite elegir cuenta distinta).
      //    GitHub muestra el selector de cuentas si el user está logueado
      //    con múltiples cuentas en el navegador, o el login form si no.
      await handleConnect();
    } catch (e) {
      setError(es ? 'Error al cambiar de cuenta' : 'Failed to switch account');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    if (!files || files.length === 0) {
      setError(es ? 'No hay archivos para guardar' : 'No files to save');
      return;
    }
    setLoading(true);
    try {
      const body = {
        commit_message: commitMessage,
        branch: 'main',
        files: files.map((f) => ({
          path: f.path,
          content: typeof f.content === 'string' ? f.content : String(f.content || ''),
          encoding: 'utf-8',
        })),
      };
      if (mode === 'new') {
        // 🟥 P19.1 · Enviar el nombre SANITIZADO al backend (coincide con el
        // preview que el usuario vio). Evita errores 422 de GitHub por espacios/chars inválidos.
        const clean = sanitizeRepoName(newRepoName) || sanitizeRepoName(defaultRepoName);
        body.new_repo_name = clean;
        body.private = privateRepo;
      } else {
        body.repo_full_name = selectedRepo;
      }
      const data = await fetchJSON('/api/github/save-project', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setSuccess({
        repoUrl: data.repo_url,
        commitUrl: data.commit_url,
        owner: data.owner,
        repo: data.repo,
      });
    } catch (e) {
      const detail = e?.data?.detail || e?.message || '';
      let msg;
      if (detail.includes('github_premium_required')) {
        msg = es ? 'Esta función es solo para Premium' : 'This feature is Premium-only';
      } else if (detail.includes('github_not_connected')) {
        msg = es ? 'GitHub no conectado' : 'GitHub not connected';
        onRefreshStatus?.();
      } else if (detail.includes('create_repo_failed')) {
        msg = es
          ? 'Error al crear el repositorio (¿nombre duplicado?)'
          : 'Failed to create repository (duplicate name?)';
      } else {
        msg = es ? `Error al guardar: ${detail}` : `Save error: ${detail}`;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // 🟥 P19 2026-07-25 · Fix modal detrás del header/dashboard.
  // Marcelo reportó que al abrir "Guardar en GitHub" el modal aparecía
  // detrás del header de la plataforma. Root cause: el componente padre
  // crea un stacking context propio (position/transform), y z-[9999] solo
  // eleva DENTRO de ese contexto, no globalmente.
  // Fix: React Portal → el modal se renderiza directo en document.body,
  // fuera de cualquier stacking context padre → siempre queda por encima.
  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
      onClick={() => !loading && onClose?.()}
      data-testid="save-to-github-modal"
    >
      <div
        className="w-full max-w-lg rounded-xl bg-zinc-900 border border-zinc-800 p-6 text-zinc-100 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Github className="w-5 h-5" />
            {es ? 'Guardar en GitHub' : 'Save to GitHub'}
          </h3>
          <button
            type="button"
            onClick={() => !loading && onClose?.()}
            className="text-zinc-400 hover:text-white"
            aria-label="close"
            data-testid="save-to-github-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Estado 1: no conectado */}
        {!connected && (
          <div className="space-y-3 text-sm">
            {/* 🟥 P19.1 · Banner "última cuenta conectada" para reconexión rápida. */}
            {lastKnownUsername && (
              <div
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs"
                data-testid="last-github-username-banner"
              >
                <span className="text-emerald-300">
                  {es ? 'Última cuenta usada:' : 'Last used account:'}{' '}
                  <strong data-testid="last-github-username-value">@{lastKnownUsername}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    try { window.localStorage?.removeItem(LAST_USERNAME_LS_KEY); } catch (_) { /* noop */ }
                    setLastKnownUsername(null);
                  }}
                  className="text-emerald-300/60 hover:text-emerald-300 text-[10px] underline"
                  data-testid="clear-last-github-username"
                >
                  {es ? 'olvidar' : 'forget'}
                </button>
              </div>
            )}
            <p className="text-zinc-400">
              {es
                ? 'Conectá tu cuenta de GitHub para guardar el proyecto en un repositorio.'
                : 'Connect your GitHub account to save the project to a repository.'}
            </p>
            <button
              type="button"
              onClick={handleConnect}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
              data-testid="connect-github-btn"
            >
              <Github className="w-4 h-4" />
              {lastKnownUsername
                ? (es ? `Reconectar como @${lastKnownUsername}` : `Reconnect as @${lastKnownUsername}`)
                : (es ? 'Conectar con GitHub' : 'Connect with GitHub')}
            </button>
            <p className="text-[10px] text-zinc-500 leading-relaxed">
              {es
                ? 'Se usa OAuth oficial. No se piden tokens manuales.'
                : 'Official OAuth flow. No manual tokens required.'}
            </p>
          </div>
        )}

        {/* Estado 2: conectado pero sin scope repo */}
        {connected && !scopeOk && (
          <div className="space-y-3 text-sm">
            <p className="text-amber-400">
              {es
                ? 'Conectado, pero falta autorizar el permiso "repo".'
                : 'Connected, but missing "repo" permission.'}
            </p>
            <button
              type="button"
              onClick={handleConnect}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-zinc-900 font-medium hover:bg-amber-400"
              data-testid="reauth-github-btn"
            >
              {es ? 'Re-autorizar GitHub' : 'Re-authorize GitHub'}
            </button>
          </div>
        )}

        {/* Estado 3: conectado y con permisos */}
        {connected && scopeOk && !success && (
          <div className="space-y-4 text-sm">
            {/* 🟥 P19.2 · Header de cuenta conectada con acciones claras.
                Antes: solo un link "Desconectar" muy discreto.
                Ahora: 2 botones pequeños pero visibles con íconos y texto:
                - "Cambiar cuenta" (desconecta + reconecta con otra cuenta)
                - "Desconectar" (solo desconecta) */}
            <div className="flex flex-wrap items-center justify-between gap-2 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              {ghLogin && (
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-emerald-300">
                    {es ? 'Conectado como' : 'Connected as'}
                  </span>
                  <strong className="text-white" data-testid="github-connected-username">@{ghLogin}</strong>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleSwitchAccount}
                  disabled={loading}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-zinc-800 hover:bg-indigo-600/20 text-zinc-300 hover:text-indigo-300 border border-zinc-700 hover:border-indigo-500/40 text-[11px] font-medium transition-colors disabled:opacity-50"
                  title={es ? 'Desconectar esta cuenta y conectar con otra distinta' : 'Disconnect this account and connect with a different one'}
                  data-testid="switch-github-account-btn"
                >
                  <UserPlus className="w-3 h-3" />
                  {es ? 'Cambiar cuenta' : 'Switch account'}
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={loading}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-zinc-800 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-300 border border-zinc-700 hover:border-rose-500/40 text-[11px] font-medium transition-colors disabled:opacity-50"
                  title={es ? 'Desconectar sin reconectar' : 'Disconnect without reconnecting'}
                  data-testid="disconnect-github-btn"
                >
                  <LogOut className="w-3 h-3" />
                  {es ? 'Desconectar' : 'Disconnect'}
                </button>
              </div>
            </div>

            {!hasActiveProject && (
              <p className="text-xs text-zinc-400 bg-zinc-800/50 border border-zinc-700 rounded p-2">
                {es
                  ? 'No hay proyecto activo. Generá uno en el Workspace para guardarlo en GitHub.'
                  : 'No active project. Generate one in the Workspace to save it to GitHub.'}
              </p>
            )}

            {hasActiveProject && (
              <>
                <div className="flex gap-2 border-b border-zinc-800 pb-2">
                  <button
                    type="button"
                    onClick={() => setMode('new')}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      mode === 'new'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                    data-testid="tab-new-repo"
                  >
                    {es ? 'Repo nuevo' : 'New repo'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('existing')}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      mode === 'existing'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                    data-testid="tab-existing-repo"
                  >
                    {es ? 'Repo existente' : 'Existing repo'}
                  </button>
                </div>

                {mode === 'new' && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-zinc-400">
                      {es ? 'Nombre del nuevo repositorio' : 'New repository name'}
                    </label>
                    <input
                      type="text"
                      value={newRepoName}
                      onChange={(e) => setNewRepoName(e.target.value)}
                      className="w-full px-3 py-2 rounded bg-zinc-800 border border-zinc-700 text-white text-sm"
                      placeholder="my-project"
                      data-testid="new-repo-name-input"
                    />
                    <label className="flex items-center gap-2 text-xs text-zinc-400">
                      <input
                        type="checkbox"
                        checked={privateRepo}
                        onChange={(e) => setPrivateRepo(e.target.checked)}
                        data-testid="new-repo-private-checkbox"
                      />
                      {es ? 'Repositorio privado' : 'Private repository'}
                    </label>

                    {/* 🟥 P19.1 · Preview del repositorio (URL + visibilidad + advertencia).
                        Muestra en tiempo real cómo va a quedar el repo antes de crearlo. */}
                    {(() => {
                      const cleanName = sanitizeRepoName(newRepoName);
                      const owner = ghLogin || (es ? 'tu-usuario' : 'your-username');
                      const nameChanged = cleanName && cleanName !== newRepoName;
                      const nameEmpty = !cleanName;
                      const alreadyExists = cleanName && repos.some(
                        (r) => (r.name || '').toLowerCase() === cleanName.toLowerCase()
                      );
                      return (
                        <div
                          className="mt-2 p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-1.5"
                          data-testid="new-repo-preview"
                        >
                          <div className="flex items-center justify-between gap-2 text-[11px]">
                            <span className="text-zinc-500 uppercase tracking-wide font-semibold">
                              {es ? 'Vista previa' : 'Preview'}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                privateRepo
                                  ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                                  : 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
                              }`}
                              data-testid="new-repo-visibility-badge"
                            >
                              {privateRepo
                                ? (es ? '🔒 Privado' : '🔒 Private')
                                : (es ? '🌐 Público' : '🌐 Public')}
                            </span>
                          </div>
                          <div
                            className="font-mono text-xs text-emerald-300 break-all"
                            data-testid="new-repo-preview-url"
                          >
                            github.com/{owner}/
                            <strong className="text-white">{cleanName || (es ? '(nombre requerido)' : '(name required)')}</strong>
                          </div>
                          {nameEmpty && (
                            <p className="text-[10px] text-rose-400" data-testid="preview-warn-empty">
                              {es
                                ? '⚠ El nombre no puede estar vacío.'
                                : '⚠ Name cannot be empty.'}
                            </p>
                          )}
                          {nameChanged && !nameEmpty && (
                            <p className="text-[10px] text-amber-400" data-testid="preview-warn-sanitized">
                              {es
                                ? `⚠ GitHub usará: ${cleanName} (se sanitizaron espacios/caracteres especiales)`
                                : `⚠ GitHub will use: ${cleanName} (spaces/special chars were sanitized)`}
                            </p>
                          )}
                          {alreadyExists && (
                            <p className="text-[10px] text-rose-400" data-testid="preview-warn-exists">
                              {es
                                ? `⚠ Ya tenés un repo llamado "${cleanName}". Elegí otro nombre o usá "Repo existente".`
                                : `⚠ You already have a repo named "${cleanName}". Pick another name or use "Existing repo".`}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {mode === 'existing' && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-zinc-400">
                      {es ? 'Elegí un repositorio' : 'Choose a repository'}
                    </label>
                    {repos.length === 0 ? (
                      <p className="text-xs text-zinc-500">
                        {es ? 'Sin repositorios disponibles' : 'No repositories available'}
                      </p>
                    ) : (
                      <select
                        value={selectedRepo}
                        onChange={(e) => setSelectedRepo(e.target.value)}
                        className="w-full px-3 py-2 rounded bg-zinc-800 border border-zinc-700 text-white text-sm"
                        data-testid="existing-repo-select"
                      >
                        {repos.map((r) => (
                          <option key={r.id} value={r.full_name}>
                            {r.full_name}{r.private ? ' (private)' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-zinc-400">
                    {es ? 'Mensaje del commit' : 'Commit message'}
                  </label>
                  <input
                    type="text"
                    value={commitMessage}
                    onChange={(e) => setCommitMessage(e.target.value)}
                    className="w-full px-3 py-2 rounded bg-zinc-800 border border-zinc-700 text-white text-sm"
                    data-testid="commit-message-input"
                  />
                </div>

                <p className="text-xs text-zinc-500">
                  {es ? 'Archivos a subir: ' : 'Files to upload: '}
                  <strong className="text-zinc-300">{files.length}</strong>
                </p>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading || (mode === 'existing' && !selectedRepo)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-save-github-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Github className="w-4 h-4" />}
                  {loading
                    ? (es ? 'Guardando…' : 'Saving…')
                    : (es ? 'Guardar en GitHub' : 'Save to GitHub')}
                </button>
              </>
            )}
          </div>
        )}

        {/* Estado 4: éxito — CUADRO NEGRO: removido el texto verde "✓ Repositorio guardado"; quedan solo los links funcionales para navegar al repo/commit. */}
        {success && (
          <div className="space-y-3 text-sm" data-testid="save-github-success">
            <p className="text-zinc-300">
              <strong>{success.owner}/{success.repo}</strong>
            </p>
            <div className="flex flex-col gap-2">
              <a
                href={success.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 text-xs"
              >
                <ExternalLink className="w-3 h-3" />
                {es ? 'Abrir repositorio' : 'Open repository'}
              </a>
              <a
                href={success.commitUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 text-xs"
              >
                <ExternalLink className="w-3 h-3" />
                {es ? 'Ver commit' : 'View commit'}
              </a>
            </div>
            <button
              type="button"
              onClick={() => { setSuccess(null); onClose?.(); }}
              className="w-full px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm"
              data-testid="close-success-btn"
            >
              {es ? 'Cerrar' : 'Close'}
            </button>
          </div>
        )}

        {error && !success && (
          <p
            className="mt-3 text-xs text-rose-400 bg-rose-900/20 border border-rose-900/40 rounded p-2"
            data-testid="github-error-msg"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );

  // Renderizar directamente en document.body para escapar de cualquier
  // stacking context padre (fix P19 · GitHub modal detrás del dashboard).
  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export default SaveToGitHubModal;
